"use client";

import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import { useMemo, useState } from "react";
import { Typography } from "@mui/material";

interface ScaleButtonGroupProps {
  min?: number;
  max?: number;
  value: string;
  scaleLabels?: Record<number, string>;
  onChange?: (value: string) => void;
  name?: string;
}

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 5;

export const ScaleButtonGroup = ({
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  scaleLabels = {},
  value,
  onChange = () => null,
}: ScaleButtonGroupProps) => {
  const [selected, setSelected] = useState(value);

  const numberList = useMemo(() => {
    const minValue = min < max ? min : DEFAULT_MIN;
    const maxValue = min < max ? max : DEFAULT_MAX;
    return [...Array<number>(maxValue - minValue + 1)].map(
      (_, i) => i + minValue
    );
  }, [min, max]);

  return (
    <div className="flex w-full flex-col gap-2 lg:py-2">
      <ButtonGroup
        className="mt-7 overflow-visible rounded-full"
        disableFocusRipple
        disableRipple
        fullWidth
        variant="text"
      >
        {numberList.map((v, i) => {
          const isSelected = String(selected) === String(v);
          const hasLabel = Boolean(scaleLabels[Number(v)]);

          return (
            <Button
              className="px-4 py-2 first:rounded-l-full last:rounded-r-full lg:px-6 lg:py-4"
              data-cy={v}
              key={v}
              onClick={() => {
                setSelected(String(v));
                onChange(String(v));
              }}
              sx={(theme) => {
                const coreStyle = {
                  bgcolor: isSelected
                    ? theme.palette.secondary.dark
                    : theme.palette.backgroundColor.component,
                  "&:focus": {
                    bgcolor: isSelected
                      ? theme.palette.secondary.dark
                      : theme.palette.backgroundColor.component,
                  },
                  borderTop: `1px solid ${theme.palette.neutral.main}`,
                  borderBottom: `1px solid ${theme.palette.neutral.main}`,
                };

                if (i === 0) {
                  return {
                    ...coreStyle,
                    borderLeft: `1px solid ${theme.palette.neutral.main}`,
                  };
                }

                if (i === numberList.length - 1) {
                  return {
                    ...coreStyle,
                    borderRight: `1px solid ${theme.palette.neutral.main}`,
                  };
                }

                return coreStyle;
              }}
            >
              {Boolean(hasLabel) && (
                <Typography
                  className="absolute -top-7 whitespace-nowrap"
                  color="text.primary"
                  variant="bodyS"
                >
                  {scaleLabels[Number(v)]}
                </Typography>
              )}
              <Typography
                color={isSelected ? "secondary.contrastText" : "text.primary"}
                variant="text_md_m"
              >
                {v}
              </Typography>
            </Button>
          );
        })}
      </ButtonGroup>
    </div>
  );
};
